/* ==========================================================================
   billService

   REAL MODE: bills are read from the `bills` table, filtered explicitly
   by estateContext.activeEstateId (see meterService's header comment for
   why). Creating a bill inserts a row directly — protected by the
   bills_insert_admin_only RLS policy (see supabase/schema.sql), so a
   non-admin calling this function directly would be rejected by the
   database regardless of what the UI shows. Payment goes through the
   pay_bill Postgres function, which also takes the target estate
   explicitly and independently re-checks the caller may act on it — same
   atomicity reasoning as walletService.withdraw.
   ========================================================================== */

import { config } from "../config";
import { getSupabase } from "./supabaseClient";
import { mapBillRow } from "./supabaseMappers";
import { mockDb } from "./mock/mockDb";
import { delay, genReference, shouldSimulateFailure } from "./mock/mockUtils";
import { requireActiveEstateId } from "../state/estateContext";
import type { Bill, CreateBillRequest, PayBillResult } from "../types";

async function listBills(): Promise<Bill[]> {
  const estateId = requireActiveEstateId();

  if (config.useMockServices) {
    await delay(400);
    return mockDb.getBills(estateId);
  }
  const { data, error } = await getSupabase()
    .from("bills")
    .select("*")
    .eq("estate_id", estateId)
    .order("due_date", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []).map(mapBillRow);
}

async function createBill(req: CreateBillRequest): Promise<Bill> {
  const estateId = requireActiveEstateId();

  if (!req.title.trim() || req.amountKobo <= 0 || !req.dueDate) {
    throw new Error("Fill in a title, a positive amount, and a due date.");
  }

  if (config.useMockServices) {
    await delay(600);
    return mockDb.createBill({
      estateId,
      title: req.title.trim(),
      category: req.category.trim() || "General",
      amountKobo: req.amountKobo,
      dueDate: req.dueDate,
    });
  }

  const { data, error } = await getSupabase()
    .from("bills")
    .insert({
      estate_id: estateId,
      title: req.title.trim(),
      category: req.category.trim() || "General",
      amount_kobo: req.amountKobo,
      due_date: req.dueDate,
    })
    .select()
    .single();
  if (error) throw new Error(error.message);
  return mapBillRow(data);
}

async function payBill(billId: string): Promise<PayBillResult> {
  const estateId = requireActiveEstateId();

  if (config.useMockServices) {
    const bill = mockDb.getBills(estateId).find((b) => b.id === billId);
    if (!bill) return { status: "failed", billId, reference: genReference("BILL"), amountKobo: 0, failureReason: "Bill not found." };
    if (bill.status === "paid") {
      return { status: "failed", billId, reference: genReference("BILL"), amountKobo: bill.amountKobo, failureReason: "This bill has already been paid." };
    }

    const currentBalance = mockDb.getWalletBalanceKobo(estateId);
    if (bill.amountKobo > currentBalance) {
      return { status: "failed", billId, reference: genReference("BILL"), amountKobo: bill.amountKobo, failureReason: "Insufficient wallet balance." };
    }

    await delay(900);

    if (shouldSimulateFailure()) {
      return { status: "failed", billId, reference: genReference("BILL"), amountKobo: bill.amountKobo, failureReason: "Bill payment declined (simulated)." };
    }

    mockDb.debitWallet(estateId, bill.amountKobo);
    mockDb.markBillPaid(billId);

    const reference = genReference("BILL");
    mockDb.addTransaction(estateId, {
      type: "bill_payment",
      status: "success",
      amountKobo: bill.amountKobo,
      direction: "debit",
      description: bill.title,
      reference,
      billId,
    });

    return { status: "success", billId, reference, amountKobo: bill.amountKobo };
  }

  const supabase = getSupabase();
  const { data: billRow, error: billErr } = await supabase
    .from("bills")
    .select("amount_kobo, status")
    .eq("id", billId)
    .single();
  if (billErr || !billRow) {
    return { status: "failed", billId, reference: genReference("BILL"), amountKobo: 0, failureReason: "Bill not found." };
  }
  if (billRow.status === "paid") {
    return {
      status: "failed",
      billId,
      reference: genReference("BILL"),
      amountKobo: billRow.amount_kobo,
      failureReason: "This bill has already been paid.",
    };
  }

  const reference = genReference("BILL");
  const { error } = await supabase.rpc("pay_bill", { p_bill_id: billId, p_reference: reference, p_estate_id: estateId });
  if (error) {
    return { status: "failed", billId, reference, amountKobo: billRow.amount_kobo, failureReason: error.message };
  }
  return { status: "success", billId, reference, amountKobo: billRow.amount_kobo };
}

/** Asks the database to issue any bills that have come due (periodic bills,
 *  payment-plan instalments, annual bills). Safe to call every time the app
 *  opens; it does nothing when nothing is due. Never throws. */
async function generateDue(): Promise<void> {
  if (config.useMockServices) return;
  try {
    await getSupabase().rpc("generate_due_bills");
  } catch {
    /* not fatal: the bills list below still loads whatever exists */
  }
}

export const billService = { listBills, createBill, payBill, generateDue };
